import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { DepartureHit } from "@wojo/services";
import { StatusLabel, Text } from "@wojo/ui";
import cn from "clsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { LuCalendarHeart } from "react-icons/lu";
import {
    TripBuilderDepartureDetailType,
    useTourPageContext,
} from "../../../_client/context";
import { scrollToSection } from "../../../_client/hooks";
import { useDeparturesStepContext } from "../context/DeparturesStepContext";
import { useTripBuilder } from "../context/TripBuilderContext";
import { DepartureDateAvailabilityBadge } from "./DepartureDateAvailabilityBadge";
import { SpecialDepartureModal } from "./SpecialDepartureModal";
import styles from "./TripBuilderDeparturesStep.module.scss";
import { PlaceType } from "../../../../generated/graphql";
import { useUrgencyBadgesExperiment } from "@wojo/services";
import { useTrackEvent } from "@optimizely/react-sdk";

dayjs.extend(utc);

export type DepartureListDateCellProps = {
    departure: DepartureHit;
};

const formatDate = (start: number, end: number) => {
    return `${dayjs.unix(start).utc().format("MMM D")} – 
    ${dayjs.unix(end).utc().format("MMM D")}`;
};

export const DepartureListDateCell: React.FC<DepartureListDateCellProps> = ({
    departure,
}) => {
    const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
    const { specialDepartures, tourContent, colorPalette, navRef } =
        useTourPageContext();
    const { setDepartureId, departureDetailType, gcDeparture } =
        useTripBuilder();
    const router = useRouter();
    const { productPage } = useI18n("productPage");
    const { filterStates } = useDeparturesStepContext();
    const redirectPath = `${departure.tourPagePath}?priceWithFlightsGateway=${filterStates.priceWithFlightsGateway}&priceWithFlights=${filterStates.priceWithFlights}&departure=${departure.id}#tripbuilder`;
    const trimSlashes = (str: string): string => {
        return str.replace(/^\/+|\/+$/g, "");
    };

    const [optimizelyTrack, optimizelyClientReady] = useTrackEvent();
    const experimentBadge = useUrgencyBadgesExperiment(
        departure.departureStatusBadge,
        departure.nbSpotsLeft,
    );
    const isSpecialDeparture = !!specialDepartures?.some(
        (page) =>
            `${trimSlashes(page.link.cached_url)}` ===
            `${trimSlashes(departure.tourPagePath)}`,
    );

    const isWaitlisted = experimentBadge?.text === "Waitlisted";
    const isCallToBook = experimentBadge?.text.trim() === "Call to book";
    const showLastMinuteSpecialBadge =
        departure.isLastMinuteSpecial &&
        (!experimentBadge?.text || !isSpecialDeparture);

    const handleSelectDeparture = () => {
        gtmSendEvent({
            event: "add_to_cart",
            ga4Tracking: {
                ga4_object: "TRIP_BUILDER",
                ga4_action: "DATE_SELECTED",
                cityIDs:
                    tourContent.tour?.places
                        .filter((place) => place.type === PlaceType.City)
                        .map((place) => place._id) ?? [],
            },
            ecommerce: {
                currency: process.env.NEXT_PUBLIC_CURRENCY_CODE || "",
                value: departure.salePrice || departure.price || 0,
                items: [
                    {
                        item_id: departure.id,
                        item_name: departure.name,
                        discount: departure.discountAmount ?? 0,
                        item_brand: departure.businessCode,
                        item_variant: "Departure",
                        price: departure.price,
                        salePrice: departure.salePrice,
                        quantity: 1,
                        activityLevel: departure.activityLevel?.name || "",
                        classLevel: departure.classLevel?.name || "",
                        onSale: departure.isOnSale,
                        groupTrip: !!gcDeparture,
                        rating: departure.rating,
                        tourCode: departure.tourCode,
                        hasExtension: departure.hasExtensionAvailable,
                        prodStatus: departure.status,
                        prodBadge: experimentBadge?.text || "",
                        isWaitlist: isWaitlisted.toString(),
                        isCallToBook: isCallToBook.toString(),
                        isSpecialDeparture: isSpecialDeparture.toString(),
                    },
                ],
            },
        });
        if (optimizelyClientReady) {
            optimizelyTrack("trip_builder_date_selected");
        }
        if (departure.tourCode === tourContent.tourCode) {
            setDepartureId(departure.id);
            scrollToSection("#tripbuilder", navRef);
        } else if (isSpecialDeparture) {
            gtmSendEvent({
                event: "track",
                ga4Tracking: {
                    ga4_object: "TRIP_BUILDER",
                    ga4_action: "MODAL_OPENED",
                    modalId: "Special Departure",
                },
            });
            setIsSpecialModalOpen(true);
        } else {
            router.push(redirectPath);
        }
    };

    return (
        <td className={cn(styles["table-cell"], styles["table-cell--date"])}>
            <div
                style={{
                    display: "flex",
                    gap: "var(--g-spacing-sm)",
                    alignItems: "flex-start",
                }}
            >
                <div className={styles["button-indicator"]} />
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--g-spacing-xs)",
                        alignItems: "flex-start",
                    }}
                    id={`${departure.id}_date`}
                >
                    <button
                        className={styles.cta}
                        id={`${departure.id}_select`}
                        type="button"
                        data-testid={departure.id}
                        onClick={handleSelectDeparture}
                    >
                        <Text.Body tag="span" emphasis="2">
                            {formatDate(
                                departure.departureDate,
                                departure.endDate,
                            )}
                        </Text.Body>
                    </button>
                    {experimentBadge?.text && (
                        <DepartureDateAvailabilityBadge
                            departureStatusBadge={experimentBadge}
                        />
                    )}
                    {departure.hasExtensionAvailable &&
                        departureDetailType !==
                            TripBuilderDepartureDetailType.Extension && (
                            <Text.Body tag="span" variant="2">
                                {productPage.tripBuilder.departures.hasExtensionLabel.l()}
                            </Text.Body>
                        )}
                    {isSpecialDeparture && (
                        <Text.Body
                            tag="span"
                            variant="2"
                            style={{
                                display: "inline-flex",
                                alignItems: "flex-start",
                                gap: "var(--g-spacing-xs)",
                            }}
                        >
                            <LuCalendarHeart
                                aria-hidden
                                size={14}
                                style={{ height: "1lh" }}
                                strokeWidth={1.5}
                            />
                            {productPage.tripBuilder.departures.specialDepartureLabel.l()}
                            <SpecialDepartureModal
                                {...departure.specialDepartureContent}
                                tourPagePath={redirectPath}
                                colorPalette={colorPalette}
                                isOpen={isSpecialModalOpen}
                                onCloseRequested={() => {
                                    gtmSendEvent({
                                        event: "track",
                                        ga4Tracking: {
                                            ga4_object: "TRIP_BUILDER",
                                            ga4_action: "MODAL_CLOSED",
                                            modalId: "Special Departure",
                                        },
                                    });
                                    setIsSpecialModalOpen(false);
                                }}
                            />
                        </Text.Body>
                    )}
                    {showLastMinuteSpecialBadge && (
                        <StatusLabel
                            variant="primary"
                            status="deal"
                            size="small"
                            text={productPage.tripBuilder.departures.lastMinuteSpecialLabel.l()}
                            style={
                                experimentBadge?.text
                                    ? { marginTop: "var(--g-spacing-xs)" }
                                    : {}
                            }
                        />
                    )}
                </div>
            </div>
        </td>
    );
};
